import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LOGIN } from "@/constants/testIds";
import { AltarodaLogo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ShieldAlert } from "lucide-react";
import { formatApiError } from "@/lib/api";
import { homePathFor } from "@/lib/uiMode";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const from = location.state?.from?.pathname;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const user = await login(username.trim(), password);
      if (user.must_change_password) {
        navigate("/change-password", { replace: true });
      } else {
        navigate(from || homePathFor(user), { replace: true });
      }
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid={LOGIN.root}
      className="min-h-screen grid lg:grid-cols-[minmax(0,1fr)_520px]"
    >
      {/* Visual side (dark, industrial) */}
      <div
        className="hidden lg:flex relative flex-col justify-between p-10 text-white overflow-hidden"
        style={{ backgroundColor: "#171717" }}
      >
        <div className="relative z-10">
          <AltarodaLogo variant="light" size={26} />
        </div>
        <div className="relative z-10 max-w-md">
          <div
            className="inline-block w-8 h-[3px] mb-4"
            style={{ backgroundColor: "#CD013A" }}
          />
          <h1
            className="text-3xl font-bold leading-tight mb-3"
            style={{ letterSpacing: "-0.02em" }}
          >
            ALTARODA
          </h1>
          <p className="text-white/70 text-sm leading-relaxed">
            Plataforma interna de gestão de pedidos de assistência técnica,
            manutenção e instalação. Coordenação, rastreabilidade e prazos —
            num único lugar.
          </p>
        </div>
        <div className="relative z-10 text-[11px] text-white/40 uppercase tracking-widest">
          © ALTARODA · Uso interno
        </div>
        {/* Subtle industrial grid overlay */}
        <div
          className="absolute inset-0 opacity-[0.06] pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
            backgroundSize: "32px 32px",
          }}
        />
        {/* Red accent bar */}
        <div
          className="absolute right-0 top-0 h-full w-[3px]"
          style={{ backgroundColor: "#CD013A" }}
        />
      </div>

      {/* Form side */}
      <div className="flex items-center justify-center p-6 sm:p-10 bg-surface-bg">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8">
            <AltarodaLogo variant="dark" size={22} />
          </div>
          <div className="mb-6">
            <div
              className="inline-block w-8 h-[3px] mb-3"
              style={{ backgroundColor: "#CD013A" }}
            />
            <h2 className="text-2xl font-semibold text-ink-primary">
              Entrar na ALTARODA
            </h2>
            <p className="text-sm text-ink-secondary mt-1">
              Introduza as suas credenciais para continuar.
            </p>
          </div>

          <form onSubmit={submit} className="space-y-4" noValidate>
            <div className="space-y-1.5">
              <Label htmlFor="username" className="text-sm">
                Username
              </Label>
              <Input
                id="username"
                data-testid={LOGIN.username}
                type="text"
                autoComplete="username"
                autoCapitalize="none"
                spellCheck="false"
                placeholder="p.ex. joao.silva"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                className="h-11"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-sm">
                Password
              </Label>
              <Input
                id="password"
                data-testid={LOGIN.password}
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="h-11"
              />
            </div>

            {error && (
              <div
                data-testid={LOGIN.error}
                className="flex items-start gap-2 rounded-[10px] border px-3 py-2 text-sm"
                style={{
                  backgroundColor: "#FEF2F2",
                  color: "#B91C1C",
                  borderColor: "#FCA5A5",
                }}
                role="alert"
              >
                <ShieldAlert size={16} strokeWidth={1.5} className="mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            <Button
              type="submit"
              data-testid={LOGIN.submit}
              disabled={busy}
              className="w-full h-11 bg-[#171717] hover:bg-[#2d2d2d] text-white font-medium"
            >
              {busy ? (
                <>
                  <Loader2 size={16} className="animate-spin mr-2" />A entrar…
                </>
              ) : (
                "Entrar"
              )}
            </Button>
          </form>

          <p
            data-testid={LOGIN.recoverHint}
            className="mt-6 text-xs text-ink-secondary text-center"
          >
            Para recuperar o acesso, contacte o Administrador da ALTARODA.
          </p>
        </div>
      </div>
    </div>
  );
}
